import { useEffect, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import { socialModerationAPI } from '../services/api';
import { Spinner } from './loading';
import ImageLightbox from './ImageLightbox';

/**
 * Blob URL for an image served by a BEARER-GATED endpoint.
 *
 * Social Connect media is deliberately NOT on the unauthenticated
 * /api/images/view/* route that the other report pages use via getImageViewUrl.
 * Its own endpoint re-checks the feature key, project membership and
 * `deleted_at IS NULL` on every single request — which is precisely what makes a
 * takedown actually remove the photo instead of leaving a hotlinkable copy.
 *
 * The cost of that is this hook: the browser does not attach the Authorization
 * header to a plain `<img src>`, so such a request arrives anonymous and 401s.
 * The bytes have to be fetched through the axios instance (which carries the
 * auth interceptor) and handed to the `<img>` as an object URL.
 *
 * The object URL is revoked on unmount and on every `path` change — a moderator
 * paging through a queue opens dozens of images, and each un-revoked URL pins a
 * decoded bitmap in memory for the life of the tab.
 */
export function useAuthedImageUrl(path: string | null | undefined): {
  url: string | null;
  loading: boolean;
  failed: boolean;
  /** HTTP status of the failed fetch, 0 if the request never reached the server. */
  status: number | null;
  /** MIME type the server actually sent, on a SUCCESSFUL fetch. Empty when unknown. */
  blobType: string;
  /** Byte length of that body. 0 is the tell for "2xx but nothing came back". */
  blobSize: number | null;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState<number | null>(null);
  const [blobType, setBlobType] = useState('');
  const [blobSize, setBlobSize] = useState<number | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      setLoading(false);
      setFailed(false);
      setStatus(null);
      setBlobType('');
      setBlobSize(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    setStatus(null);
    setBlobType('');
    setBlobSize(null);
    setLoading(true);

    socialModerationAPI
      .getMedia(path)
      .then((res) => {
        // Bail BEFORE creating the URL when the effect has already been cleaned
        // up — otherwise this allocates a blob nothing will ever revoke.
        if (cancelled) return;
        const blob = res.data as Blob;
        // RECORDED so a 2xx that still shows nothing can be diagnosed from the tile.
        // A fetch can succeed and the <img> still fail to render — an empty body, or
        // a type the browser will not decode — and for a blob: URL the Blob's `type`
        // is authoritative, so the browser does no sniffing to rescue it. Without
        // these two numbers that case is indistinguishable from a broken image, which
        // is exactly the dead end this component hit in production.
        setBlobType(blob?.type || '');
        setBlobSize(typeof blob?.size === 'number' ? blob.size : null);
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err: any) => {
        if (cancelled) return;
        // KEEP THE STATUS. A broken-image icon is the same picture whether the blob is
        // missing, the caller lacks moderator scope, or the handler threw — and telling
        // those apart previously cost a round trip through the browser's Network tab.
        // The three answers are genuinely different problems:
        //   404 → the blob is not in the container this API is configured against
        //   403 → moderator scope refused for this project
        //   5xx → the handler threw; the API log names it
        setFailed(true);
        setStatus(Number(err?.response?.status) || 0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return { url, loading, failed, status, blobType, blobSize };
}

/**
 * The same hook under the name that says what it actually does.
 *
 * Nothing in useAuthedImageUrl is image-specific — it fetches a bearer-gated path as a
 * blob and hands back an object URL — and a DM voice note reaches the moderation
 * console down exactly that road, because `<audio src>` drops the Authorization header
 * for the same reason `<img src>` does. Aliased rather than renamed so the dozen
 * existing image call sites stay put.
 *
 * The blob's `type` is the response's Content-Type, so what the server serves is what
 * the browser's decoder is handed: an 'audio/m4a' blob is one no player will open,
 * which is precisely what safeDmContentType corrects on the way out.
 */
export const useAuthedMediaUrl = useAuthedImageUrl;

interface AuthedImageProps {
  /** API-relative media path, e.g. '/social-moderation/media/<id>?thumb=1'. */
  path: string | null | undefined;
  alt: string;
  /** Applied to the `<img>` AND to the loading / failed placeholder, so the grid
   *  does not reflow between states. */
  className?: string;
  onClick?: () => void;
}

/** `<img>` for a bearer-gated media endpoint. See useAuthedImageUrl. */
/** Why the fetch failed, in words a moderator can act on or repeat to an engineer. */
const failureReason = (status: number | null): string => {
  if (status === 404) return 'Image not found in storage (404)';
  if (status === 403) return 'Not permitted to view this image (403)';
  if (status === 401) return 'Session expired — sign in again (401)';
  if (status === 0) return 'Could not reach the server';
  if (status && status >= 500) return `Server error loading image (${status})`;
  return 'Image could not be loaded';
};

export default function AuthedImage({ path, alt, className = '', onClick }: AuthedImageProps) {
  const { url, loading, failed, status, blobType, blobSize } = useAuthedImageUrl(path);

  // THE FETCH SUCCEEDING IS NOT THE SAME AS THE IMAGE RENDERING, and until this state
  // existed the difference was invisible: a 2xx whose bytes the browser then refused to
  // decode fell straight through to the native broken-image glyph, which reports
  // nothing. That cost a long diagnosis on a live incident where the same photo showed
  // fine in the mobile app — React Native sniffs the payload, whereas a blob: URL is
  // decoded strictly by the Blob's `type`, so the browser has no way to recover.
  const [decodeFailed, setDecodeFailed] = useState(false);
  // Reset per image, or one bad tile poisons every later one rendered by this instance.
  useEffect(() => { setDecodeFailed(false); }, [url]);

  if (url && !decodeFailed) {
    return (
      <img
        src={url}
        alt={alt}
        className={className}
        onClick={onClick}
        onError={() => setDecodeFailed(true)}
      />
    );
  }

  // What actually arrived, in the two numbers that separate the remaining causes:
  //   0 bytes            → the handler answered 2xx with an empty body
  //   a non-image type   → the browser will not decode it, whatever the bytes are
  //   an image type + a  → genuinely corrupt or truncated data
  //   plausible size
  const decodeReason =
    blobSize === 0
      ? 'Server returned an empty image (0 bytes)'
      : `Browser could not decode this image (${blobType || 'unknown type'}, ${
          blobSize === null ? 'unknown size' : `${Math.round(blobSize / 1024)} KB`})`;

  return (
    <div
      className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className}`}
      // The reason is on the tile itself, not only in DevTools. A moderator deciding
      // whether to remove a post needs to know whether they are looking at a photo that
      // is missing, or one they merely cannot see — those lead to different actions.
      title={decodeFailed ? decodeReason : failed ? failureReason(status) : undefined}
    >
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <span className="flex flex-col items-center gap-1 p-1 text-center">
          <ImageOff className="h-4 w-4" />
          {decodeFailed ? (
            <span className="text-[10px] leading-tight">
              {blobSize === 0 ? '0 KB' : blobType || 'no type'}
            </span>
          ) : failed && status ? (
            <span className="text-[10px] leading-tight">{status}</span>
          ) : null}
        </span>
      )}
    </div>
  );
}

/**
 * Full-size view of a photo served by a bearer-gated endpoint. ImageLightbox takes a
 * plain `src`, so the full-size image is fetched as a blob first (useAuthedImageUrl)
 * and the resulting object URL handed over. Shared by Social Moderation and the
 * Onboarding history's answers.
 */
export function AuthedLightbox({ path, alt, onClose }: { path: string; alt: string; onClose: () => void }) {
  const { url, failed } = useAuthedImageUrl(path);

  if (url) return <ImageLightbox src={url} alt={alt} isOpen onClose={onClose} />;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80" onClick={onClose}>
      {failed
        ? <span className="text-sm text-white/80">Image could not be loaded.</span>
        : <Loader2 className="h-6 w-6 animate-spin text-white/80" />}
    </div>
  );
}
